import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import {
  ArrowLeft, BarChart3, TrendingUp, Trophy, Files, Calendar,
  ChevronUp, ChevronDown, Award, Target, Eye, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Storico campagne fisiche — comparison dashboard for concluded campaigns.
 *
 * Reuses the same data shape returned by `/api/admin/campaigns` (each
 * row already carries `total_scans`, `total_assignments`, `total_redeemed`).
 * No new backend endpoint needed.
 *
 * Adds derived KPIs:
 *  - conv_pct  = assignments / scans * 100   (engagement)
 *  - redeem_pct = redeemed / assignments * 100  (effective ROI)
 *  - score     = simple ranking (avg of conv % and redeem %)
 *
 * Sortable by every column. One-click clone for re-running a winner.
 */
export default function AdminCampaignsHistory() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [sortKey, setSortKey] = useState('end_date');
  const [sortDir, setSortDir] = useState('desc');

  const load = async () => {
    setRows(null);
    try {
      const r = await adminApi.get('/admin/campaigns');
      const concluded = (r.data?.campaigns || []).filter(c => c.status === 'concluded');
      const enriched = concluded.map(c => {
        const scans = c.total_scans || 0;
        const assigns = c.total_assignments || 0;
        const redeemed = c.total_redeemed || 0;
        const conv = scans > 0 ? (assigns / scans) * 100 : 0;
        const redeem = assigns > 0 ? (redeemed / assigns) * 100 : 0;
        return {
          ...c,
          _scans: scans, _assigns: assigns, _redeemed: redeemed,
          _conv: conv, _redeem: redeem,
          _score: (conv + redeem) / 2,
        };
      });
      setRows(enriched);
    } catch {
      toast.error('Errore caricamento storico');
      setRows([]);
    }
  };

  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => {
    if (!rows) return null;
    const dir = sortDir === 'desc' ? -1 : 1;
    const k = sortKey;
    return [...rows].sort((a, b) => {
      let av = a[k]; let bv = b[k];
      if (av === undefined || av === null || av === '') av = -Infinity;
      if (bv === undefined || bv === null || bv === '') bv = -Infinity;
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
  }, [rows, sortKey, sortDir]);

  const setSort = (k) => {
    if (sortKey === k) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  const SortHeader = ({ k, children, align = 'left' }) => (
    <button onClick={() => setSort(k)}
      className={`flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-800 ${
        align === 'right' ? 'ml-auto' : ''
      }`}
      data-testid={`sort-${k}`}>
      {children}
      {sortKey === k && (sortDir === 'desc' ? <ChevronDown size={11} /> : <ChevronUp size={11} />)}
    </button>
  );

  const handleClone = async (c) => {
    if (!window.confirm(`Clonare "${c.name}"? Verrà creata una bozza con date vuote, premi resettati e dati di scansione/assegnazione azzerati.`)) return;
    try {
      const res = await adminApi.post(`/admin/campaigns/${c.id}/clone`);
      toast.success(`Campagna clonata: ${res.data.name}. Vai a impostare le nuove date.`);
      navigate('/admin/campaigns');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore clonazione');
    }
  };

  if (rows === null) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="animate-spin text-[#D44E20]" size={28} />
      </div>
    );
  }

  // ── Aggregate KPIs ──
  const sumScans = rows.reduce((s, r) => s + r._scans, 0);
  const sumAssigns = rows.reduce((s, r) => s + r._assigns, 0);
  const sumRedeemed = rows.reduce((s, r) => s + r._redeemed, 0);
  const avgConv = rows.length ? rows.reduce((s, r) => s + r._conv, 0) / rows.length : 0;
  const avgRedeem = rows.length ? rows.reduce((s, r) => s + r._redeem, 0) / rows.length : 0;
  const best = rows.length ? rows.reduce((b, r) => r._score > (b?._score ?? -1) ? r : b, null) : null;

  return (
    <div className="space-y-6" data-testid="campaigns-history-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button onClick={() => navigate('/admin/campaigns')}
            className="text-xs text-slate-500 hover:text-[#D44E20] flex items-center gap-1 mb-2"
            data-testid="back-btn">
            <ArrowLeft size={12} /> Campagne attive
          </button>
          <h1 className="text-3xl font-bold text-[#1A1A1A]">Storico Campagne</h1>
          <p className="text-sm text-slate-500 mt-1">
            Confronta le performance delle campagne concluse e ri-attiva quelle vincenti con un click.
          </p>
        </div>
      </div>

      {/* Aggregate KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={<Eye />} label="Scan totali" value={sumScans} accent="#3B82F6" testid="kpi-scans" />
        <Kpi icon={<Target />} label="Assegnati totali" value={sumAssigns} accent="#D44E20" testid="kpi-assigns" />
        <Kpi icon={<Award />} label="Riscattati totali" value={sumRedeemed} accent="#10B981" testid="kpi-redeemed" />
        <Kpi icon={<TrendingUp />} label="Conv. media" value={`${avgConv.toFixed(1)}%`} accent="#8B5CF6" testid="kpi-avg-conv" extra={`Redeem media: ${avgRedeem.toFixed(1)}%`} />
      </div>

      {/* Best campaign banner */}
      {best && rows.length > 1 && (
        <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-4 flex items-center gap-3" data-testid="best-banner">
          <div className="w-12 h-12 rounded-full bg-amber-200/60 flex items-center justify-center flex-shrink-0">
            <Trophy size={22} className="text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Campagna più performante</p>
            <p className="text-base font-bold text-[#1A1A1A] truncate">{best.name}</p>
            <p className="text-xs text-slate-600 mt-0.5">
              Conv. {best._conv.toFixed(1)}% · Redeem {best._redeem.toFixed(1)}% · Score {best._score.toFixed(1)}
            </p>
          </div>
          <button onClick={() => handleClone(best)}
            className="bg-amber-700 hover:bg-amber-800 text-white text-xs font-bold px-4 py-2 rounded-xl flex items-center gap-1.5 flex-shrink-0"
            data-testid="clone-best-btn">
            <Files size={13} /> Riproponila
          </button>
        </div>
      )}

      {/* Comparison table */}
      {rows.length === 0 ? (
        <div className="rounded-2xl bg-white border border-slate-200 p-12 text-center">
          <BarChart3 size={32} className="mx-auto text-slate-300" />
          <p className="text-base font-bold text-[#1A1A1A] mt-3">Nessuna campagna conclusa</p>
          <p className="text-sm text-slate-500 mt-1">
            Le campagne in stato 'conclusa' compariranno qui per confronto.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-x-auto" data-testid="history-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-3 min-w-[180px]"><SortHeader k="name">Campagna</SortHeader></th>
                <th className="text-left p-3"><SortHeader k="end_date"><Calendar size={11} className="inline mr-1" />Termine</SortHeader></th>
                <th className="text-right p-3"><SortHeader k="_scans" align="right">Scan</SortHeader></th>
                <th className="text-right p-3"><SortHeader k="_assigns" align="right">Assegn.</SortHeader></th>
                <th className="text-right p-3"><SortHeader k="_redeemed" align="right">Riscatt.</SortHeader></th>
                <th className="text-right p-3"><SortHeader k="_conv" align="right">Conv. %</SortHeader></th>
                <th className="text-right p-3"><SortHeader k="_redeem" align="right">Redeem %</SortHeader></th>
                <th className="text-right p-3"><SortHeader k="_score" align="right">Score</SortHeader></th>
                <th className="text-center p-3"><span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Azioni</span></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => {
                const isWinner = best && r.id === best.id && rows.length > 1;
                return (
                  <tr key={r.id} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors ${isWinner ? 'bg-amber-50/30' : ''}`} data-testid={`history-row-${r.id}`}>
                    <td className="p-3 font-semibold text-[#1A1A1A]">
                      <div className="flex items-center gap-2">
                        {isWinner && <Trophy size={12} className="text-amber-600 flex-shrink-0" />}
                        <span className="truncate" title={r.name}>{r.name}</span>
                      </div>
                    </td>
                    <td className="p-3 text-xs text-slate-500 whitespace-nowrap">
                      {r.end_date ? new Date(r.end_date).toLocaleDateString('it-IT') : '—'}
                    </td>
                    <td className="p-3 text-right tabular-nums">{r._scans}</td>
                    <td className="p-3 text-right tabular-nums">{r._assigns}</td>
                    <td className="p-3 text-right tabular-nums font-semibold text-emerald-700">{r._redeemed}</td>
                    <td className="p-3 text-right tabular-nums">{r._conv.toFixed(1)}%</td>
                    <td className="p-3 text-right tabular-nums">{r._redeem.toFixed(1)}%</td>
                    <td className="p-3 text-right">
                      <span className="inline-block tabular-nums font-bold px-2 py-0.5 rounded-md text-xs"
                        style={{
                          backgroundColor: r._score >= 50 ? '#10B98115' : r._score >= 25 ? '#F59E0B15' : '#EF444415',
                          color: r._score >= 50 ? '#10B981' : r._score >= 25 ? '#F59E0B' : '#EF4444',
                        }}>
                        {r._score.toFixed(1)}
                      </span>
                    </td>
                    <td className="p-3 text-center whitespace-nowrap">
                      <button onClick={() => handleClone(r)}
                        title="Clona e ri-attiva"
                        className="inline-flex items-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-[11px] font-bold px-2.5 py-1 rounded-md"
                        data-testid={`history-clone-${r.id}`}>
                        <Files size={11} /> Clona
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ icon, label, value, accent, testid, extra }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-500">
        <span style={{ color: accent }}>{React.cloneElement(icon, { size: 14 })}</span>
        <span className="text-[10px] uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <p className="text-2xl md:text-3xl font-black tabular-nums mt-1" style={{ color: accent }}>{value}</p>
      {extra && <p className="text-[11px] text-slate-400 mt-0.5">{extra}</p>}
    </div>
  );
}
